import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Bot, Check, X, Loader2, Play, FileText, Database, Globe, PenLine } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import ReactMarkdown from 'react-markdown';

export default function BotPlanDialog({ open, onOpenChange, task, onApprove, onReject }) {
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState('');

  useEffect(() => {
    if (open && task) {
      fetchPlan();
      setIsRejected(false);
      setEditedInstructions('');
    } else {
      setPlan(null);
    }
  }, [open, task?.id]);

  const fetchPlan = async () => {
    setIsLoading(true);
    setPlan(null);
    try {
      const response = await base44.functions.invoke('getBotTaskPlan', {
        task_id: task.id
      });
      const data = response.data;
      
      // The backend returns plan_summary directly at root when plan mode works
      if (data.plan_summary) {
        setPlan(data);
      } else {
        // Fallback if the function hasn't deployed yet or returned unexpected format
        setPlan({
          plan_summary: 'The AI Officer will analyze the task context and execute accordingly.',
          steps: [
            'Review task requirements and mission context',
            'Gather relevant data and artifacts',
            'Execute and produce the requested deliverable'
          ],
          expected_output: 'Task completion report or document',
          resources_used: [],
          function_name: 'executeBotTask',
          model: task.bot_execution_model || 'claude-haiku-4-5-20251001'
        });
      }
    } catch (error) {
      console.error('Plan fetch error:', error);
      setPlan({ error: error.response?.data?.error || error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    onApprove(null); // no additional context
    onOpenChange(false);
  };

  const handleReject = () => {
    setIsRejected(true);
    // Pre-fill with the plan summary for the user to edit
    setEditedInstructions(plan?.plan_summary || task?.task_description || '');
  };

  const handleResubmit = () => {
    onApprove(editedInstructions);
    onOpenChange(false);
  };

  const resourceIcons = {
    codex: <Database className="w-3 h-3" />,
    mission_chain: <FileText className="w-3 h-3" />,
    artifacts: <FileText className="w-3 h-3" />,
    bot_memory: <Database className="w-3 h-3" />,
    issues_data: <Database className="w-3 h-3" />,
    web_search: <Globe className="w-3 h-3" />,
    artifact_content: <FileText className="w-3 h-3" />
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bots-botplandialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-400" />
            AI Officer Plan
          </DialogTitle>
          {task && (
            <p className="text-sm text-slate-400 mt-1 line-clamp-2">
              Task: {task.task_description}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
              <p className="text-sm text-slate-400">Generating execution plan...</p>
            </div>
          )}

          {plan?.error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
              Error: {plan.error}
            </div>
          )}

          {plan && !plan.error && !isRejected && (
            <>
              {/* Plan Summary */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Execution Plan</h3>
                <div className="text-sm text-slate-200 leading-relaxed prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{plan.plan_summary}</ReactMarkdown>
                </div>
              </div>

              {/* Steps */}
              {plan.steps && plan.steps.length > 0 && (
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Steps</h3>
                  <ol className="space-y-2">
                    {plan.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                        <Badge className="bg-indigo-600 text-white font-bold text-xs h-5 w-5 flex items-center justify-center p-0 flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </Badge>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Resources Used */}
              {plan.resources_used && plan.resources_used.length > 0 && (
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Resources & Context</h3>
                  <div className="flex flex-wrap gap-2">
                    {plan.resources_used.map((resource, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs text-slate-300 border-slate-600 flex items-center gap-1.5">
                        {resourceIcons[resource.type] || <FileText className="w-3 h-3" />}
                        {resource.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Function Info */}
              {plan.function_name && (
                <div className="p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/30 space-y-2">
                  <h3 className="text-sm font-semibold text-indigo-300 uppercase tracking-wider">Execution Function</h3>
                  <p className="text-sm text-slate-300 font-mono">{plan.function_name}</p>
                  {plan.model && (
                    <p className="text-xs text-slate-400">Model: {plan.model}</p>
                  )}
                </div>
              )}

              {/* Expected Output */}
              {plan.expected_output && (
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-2">
                  <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Expected Output</h3>
                  <p className="text-sm text-slate-300">{plan.expected_output}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => onOpenChange(false)}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  variant="outline"
                  className="flex-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  Revise
                </Button>
                <Button
                  onClick={handleApprove}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Approve & Execute
                </Button>
              </div>
            </>
          )}

          {/* Rejection / Edit Mode */}
          {isRejected && (
            <>
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
                <h3 className="text-sm font-semibold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                  <PenLine className="w-4 h-4" />
                  Revise Instructions
                </h3>
                <p className="text-xs text-slate-400">
                  Edit the instructions below and resubmit. The bot will use these revised instructions instead of the original task.
                </p>
                <Textarea data-testid="bots-botplandialog-textarea"
                  value={editedInstructions}
                  onChange={(e) => setEditedInstructions(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100 min-h-[150px]"
                  placeholder="Enter revised instructions for the bot..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setIsRejected(false)}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  Back to Plan
                </Button>
                <Button
                  onClick={handleResubmit}
                  disabled={!editedInstructions.trim()}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Execute with Revisions
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}